import constants, { SpecialClaimInfo } from '../constants/jsonXMLConstants';
import settings from '../constants/jsonXMLsettings';
import { JsonFlowchartNode } from './flowchartToGraph';
import { BranchVarMetadata } from './jsonFlowchartStat';
import { BuildSettings, extractSettingFieldValue, getNextValidID, getTenantSettings } from './utils';
const xmlStrings = constants.customPolicySchema.xmlStrings;

// Represents a logical "step" executing a technical profile. Could be either an orch step or subflow VTP item.
// Contains all the data needed to create either correctly.
export interface JE_DraftFlowStep {
  node: JsonFlowchartNode;
  tpId: string;
  claimsExchangeId: string;
  journeyId: string;
  preconditions?: JE_FQPreconditionSpec[];
  transferOutputClaims?: string[];
}

export interface JE_FQEnumerationSpec {
  text: string;
  value: string;
  selectByDefault: string;
}

export type JE_MaskType = 'Simple' | 'Regex';

export interface JE_FQClaimMaskSpec {
  type: JE_MaskType;
  value: string;
  regex?: string;
}

export interface JE_FQRegexPatternSpec {
  regex: string;
  helpText: string;
}

export interface JE_FQClaimSpec {
  id: string;
  dataType: string; // 'string' or 'boolean'
  displayName: string;
  userInputType?: string; // 'Readonly' | 'TextBox' | 'DropdownSingleSelect' | 'RadioSingleSelect' | 'CheckboxMultiSelect'
  restriction?: boolean;
  enumeration?: JE_FQEnumerationSpec[];
  pattern?: JE_FQRegexPatternSpec;
  mask?: JE_FQClaimMaskSpec;
  predicateValidationReference?: string;
}

export interface JE_ClaimStore {
  claimIds: Set<string>;
  declaredClaimIds: Set<string>;
  claims: { [claimId: string]: JE_FQClaimSpec };
}

export const createFQClaim = (claimStore: JE_ClaimStore, initClaim: JE_FQClaimSpec) => {
  initClaim.id = getNextValidID(initClaim.id, claimStore.claimIds);
  claimStore.claims[initClaim.id] = initClaim;
  claimStore.claimIds.add(initClaim.id.toLowerCase());
  return initClaim;
};

export const cloneFQClaim = (
  claimStore: JE_ClaimStore,
  baseClaim: JE_FQClaimSpec,
  cloneId: string,
  cloneUserInputType: string,
) => {
  let clonedClaim: JE_FQClaimSpec = {
    id: cloneId,
    displayName: baseClaim.displayName,
    dataType: baseClaim.dataType,
    userInputType: cloneUserInputType,
  };
  if (baseClaim.mask) {
    clonedClaim.mask = {
      type: baseClaim.mask.type,
      value: baseClaim.mask.value,
    };
    if (baseClaim.mask.type === 'Regex') {
      clonedClaim.mask.regex = baseClaim.mask.regex;
    }
  }
  return createFQClaim(claimStore, clonedClaim);
};

export interface JE_claimXformIOClaimSpec {
  claimId: string;
  xformClaimType: string;
}

export interface JE_claimXformParamSpec {
  id: string;
  dataType: string;
  value: string;
}

export interface JE_FQClaimXformSpec {
  id: string;
  method: string;
  inputClaims?: JE_claimXformIOClaimSpec[];
  inputParameters?: JE_claimXformParamSpec[];
  outputClaims?: JE_claimXformIOClaimSpec[];
}

export interface JE_ClaimTransformStore {
  claimXformIds: Set<string>;
  claimXforms: { [xformId: string]: JE_FQClaimXformSpec };
}

export const createFQClaimXform = (xformStore: JE_ClaimTransformStore, initXform: JE_FQClaimXformSpec) => {
  initXform.id = getNextValidID(initXform.id, xformStore.claimXformIds);
  xformStore.claimXforms[initXform.id] = initXform;
  xformStore.claimXformIds.add(initXform.id.toLowerCase());
  return initXform;
};

export interface JE_technicalProfileProtocolSpec {
  name: string;
  handler?: string;
}

export interface JE_FQMetadataItemSpec {
  key: string;
  value: string;
}

export interface JE_technicalProfileClaimRef {
  claimId?: string;
  defaultValue?: string;
  partnerClaimType?: string;
  alwaysUseDefaultValue?: boolean;
  required?: boolean;
  displayControlId?: string;
  controlClaimType?: string;
}

export interface JE_CryptographicKeyRef {
  id: string;
  storageReferenceId: string;
}

export interface JE_ValidationTechnicalProfileRef {
  profileId: string;
  continueOnError?: boolean;
  continueOnSuccess?: boolean;
  preconditions?: JE_FQPreconditionSpec[];
}

export interface JE_FQSubjectNamingInfoSpec {
  claimType: string;
  format?: string;
  excludeAsClaim?: boolean;
}

export interface JE_FQTechnicalProfileSpec {
  id: string;
  displayName?: string;
  protocol?: JE_technicalProfileProtocolSpec;
  inputTokenFormat?: string;
  metadata?: JE_FQMetadataItemSpec[];
  inputClaimXformIds?: string[];
  inputClaims?: JE_technicalProfileClaimRef[];
  displayClaims?: JE_technicalProfileClaimRef[];
  persistedClaims?: JE_technicalProfileClaimRef[];
  outputClaims?: JE_technicalProfileClaimRef[];
  outputClaimXformIds?: string[];
  includeInSso?: boolean;
  includeTechnicalProfile?: string;
  useTechnicalProfileForSessionManagement?: string;
  cryptographicKeys?: JE_CryptographicKeyRef[];
  validationTechnicalProfiles?: JE_ValidationTechnicalProfileRef[];
  subjectNamingInfo?: JE_FQSubjectNamingInfoSpec;
  outputTokenFormat?: string;
}

export interface JE_FQClaimsProviderSpec {
  displayName: string;
  domain?: string;
  technicalProfiles: { [tpId: string]: JE_FQTechnicalProfileSpec };
}

export interface JE_TechnicalProfileStore {
  tpIds: Set<string>;
  providerNameIndex: { [tpId: string]: string };
  claimsProviders: { [providerName: string]: JE_FQClaimsProviderSpec };
}

export const createFQTechnicalProfile = (
  tpStore: JE_TechnicalProfileStore,
  initTP: JE_FQTechnicalProfileSpec,
  providerName: string,
) => {
  initTP.id = getNextValidID(initTP.id, tpStore.tpIds);
  if (!tpStore.claimsProviders.hasOwnProperty(providerName)) {
    tpStore.claimsProviders[providerName] = {
      displayName: providerName,
      technicalProfiles: {},
    };
  }
  tpStore.claimsProviders[providerName].technicalProfiles[initTP.id] = initTP;
  tpStore.tpIds.add(initTP.id.toLowerCase());
  tpStore.providerNameIndex[initTP.id] = providerName;
  return initTP;
};

export interface JE_FQPreconditionSpec {
  type: string;
  executeActionsIf: boolean;
  values: string[];
  action: string;
}

export interface JE_FQClaimsProviderSelectionSpec {
  targetClaimsExchangeId?: string;
  validationClaimsExchangeId?: string;
}

export interface JE_FQClaimsExchangeSpec {
  id: string;
  technicalProfileReferenceId: string;
}

export interface JE_FQOrchestrationStepSpec {
  order: number;
  type: string;
  preconditions?: JE_FQPreconditionSpec[];
  claimsProviderSelections?: JE_FQClaimsProviderSelectionSpec[];
  claimsExchanges?: JE_FQClaimsExchangeSpec[];
  cpimIssuerTechnicalProfileReferenceId?: string;
  contentDefinitionReferenceId?: string;
}

export interface JE_FQUserJourneySpec {
  id: string;
  type: string;
  policyName?: string;
  authorizationTechnicalProfileId?: string;
  branchVars: string[];
  requiresJavascript: boolean;
  steps: JE_FQOrchestrationStepSpec[];
  relyingParties?: JE_FQRelyingPartySpec[];
  samlRelyingPartyMetadataUrls?: { [tpId: string]: { urlPrefix: string, urlPostfix: string } };
}

export interface JE_FQSingleSignOnSpec {
  scope: string;
}

export interface JE_FQJourneyInsightsSpec {
  telemetryEngine: string;
  instrumentationKey: string;
  developerMode: boolean;
  clientEnabled: boolean;
  serverEnabled: boolean;
  telemetryVersion: string;
}

export interface JE_FQUserJourneyBehaviorsSpec {
  singleSignOn: JE_FQSingleSignOnSpec;
  sessionExpiryType: string;
  sessionExpiryInSeconds: string;
  scriptExecution?: string;
  journeyInsights?: JE_FQJourneyInsightsSpec;
}

export interface JE_FQEndpointReference {
  id: string;
  userJourneyReferenceId: string;
}

export interface JE_FQRelyingPartySpec {
  policyName: string;
  endpoints?: JE_FQEndpointReference[];
  defaultUserJourney: string;
  technicalProfile: JE_FQTechnicalProfileSpec;
  userJourneyBehaviors?: JE_FQUserJourneyBehaviorsSpec;
}

export interface JE_UserJourneyStore {
  journeyIds: Set<string>;
  claimsExchangeIds: Set<string>;
  userJourneys: { [index: string]: JE_FQUserJourneySpec };
}

export const addBranchVarIDToUserJourney = (
  userJourneyStore: JE_UserJourneyStore,
  branchVar: string,
  journeyId: string,
) => {
  userJourneyStore.userJourneys[journeyId].branchVars.push(branchVar);
};

export const addFQClaimsExchangeToJourneyStep = (
  userJourneyStore: JE_UserJourneyStore,
  initCex: JE_FQClaimsExchangeSpec,
  step: JE_FQOrchestrationStepSpec,
) => {
  initCex.id = getNextValidID(initCex.id, userJourneyStore.claimsExchangeIds);
  if (!step.claimsExchanges) {
    step.claimsExchanges = [];
  }
  step.claimsExchanges.push(initCex);
  userJourneyStore.claimsExchangeIds.add(initCex.id.toLowerCase());
  return initCex;
};

export const addFQOrchestrationStepToJourney = (
  userJourneyStore: JE_UserJourneyStore,
  initOrchStep: JE_FQOrchestrationStepSpec,
  journeyId: string,
) => {
  initOrchStep.order = userJourneyStore.userJourneys[journeyId].steps.length + 1;
  if (initOrchStep.claimsExchanges) {
    initOrchStep.claimsExchanges.forEach((cex: JE_FQClaimsExchangeSpec) => {
      let assignedCexId = getNextValidID(cex.id, userJourneyStore.claimsExchangeIds);
      if (initOrchStep.claimsProviderSelections) {
        // In case the ID changed of a claimsExchange and it is referenced from a claimsexchangeprovider in this step,
        // need to update it.
        initOrchStep.claimsProviderSelections.forEach((cps: JE_FQClaimsProviderSelectionSpec) => {
          if (cps.validationClaimsExchangeId === cex.id) {
            cps.validationClaimsExchangeId = assignedCexId;
          }
        });
      }
      cex.id = assignedCexId;
      userJourneyStore.claimsExchangeIds.add(assignedCexId.toLowerCase());
    });
  }
  userJourneyStore.userJourneys[journeyId].steps.push(initOrchStep);
  return initOrchStep;
};

export const addSamlMetadataUrlToJourney = (
  userJourneyStore: JE_UserJourneyStore,
  journeyId: string,
  tpId: string,
  metadataUrlA: string,
  metadataUrlB: string
) => {
  const journey = userJourneyStore.userJourneys[journeyId];
  if (journey.samlRelyingPartyMetadataUrls) {
    journey.samlRelyingPartyMetadataUrls[tpId] = { urlPrefix: metadataUrlA, urlPostfix: metadataUrlB };
  } else {
    journey.samlRelyingPartyMetadataUrls = { [tpId]: { urlPrefix: metadataUrlA, urlPostfix: metadataUrlB } };
  }
}

export const addNewUserJourney = (
  userJourneyStore: JE_UserJourneyStore,
  journeyId: string,
  journeyType: string,
  enableJavascript?: boolean,
  policyId?: string,
) => {
  userJourneyStore.userJourneys[journeyId] = {
    id: journeyId,
    type: journeyType,
    policyName: policyId,
    steps: [],
    branchVars: [],
    requiresJavascript: enableJavascript ?? false,
  };
  userJourneyStore.journeyIds.add(journeyId);
};

export const setUserJourneyAuthorizationProfile = (
  userJourneyStore: JE_UserJourneyStore,
  journeyId: string,
  profileId: string,
) => {
  userJourneyStore.userJourneys[journeyId].authorizationTechnicalProfileId = profileId;
};

export interface JE_FQLocalizedResourceRef {
  language: string;
  referenceId: string;
}

export interface JE_FQLocalizedResourceRefs {
  mergeBehavior: string;
  references: JE_FQLocalizedResourceRef[];
}

export interface JE_FQContentDefinitionSpec {
  id: string;
  loadUri: string;
  recoveryUri: string;
  dataUri: string;
  metadata: JE_FQMetadataItemSpec[];
  localizedResourcesReferences?: JE_FQLocalizedResourceRefs;
}

export interface JE_ContentDefinitionStore {
  cdIds: Set<string>;
  urlMap: { [url: string]: { [key: string]: string } };
  contentDefinitions: { [index: string]: JE_FQContentDefinitionSpec };
}

export interface JE_FQLocalizedString {
  elementType: string;
  elementId?: string;
  stringId: string;
  value: string;
}

export interface JE_FQLocalizedResourcesSpec {
  id: string;
  localizedStrings: JE_FQLocalizedString[];
}

export interface JE_FQSupportedLanguagesSpec {
  defaultLanguage: string;
  mergeBehavior: string;
  supportedLanguages: string[];
}

export interface JE_FQLocalizationSpec {
  enabled: boolean;
  supportedLanguages: JE_FQSupportedLanguagesSpec;
  localizedResources: { [index: string]: JE_FQLocalizedResourcesSpec };
}

export interface JE_FQClientDefinition {
  id: string;
  uiFilterFlags: string;
}

export const createFQClientDefinition = (cdStore: JE_ClientDefinitionStore, draftCD: JE_FQClientDefinition) => {
  draftCD.id = getNextValidID(draftCD.id, cdStore.clientDefinitionIds);
  cdStore.clientDefinitions.push(draftCD);
  cdStore.clientDefinitionIds.add(draftCD.id.toLowerCase());
  return draftCD;
};

export interface JE_ClientDefinitionStore {
  clientDefinitionIds: Set<string>;
  clientDefinitions: JE_FQClientDefinition[];
}

export interface JE_DisplayControlAction {
  id: string;
  validationClaimsExchange: JE_ValidationTechnicalProfileRef[];
}

export interface JE_FQDisplayControlSpec {
  id: string;
  userInterfaceControlType: string;
  inputClaims?: JE_technicalProfileClaimRef[];
  displayClaims?: JE_technicalProfileClaimRef[];
  outputClaims?: JE_technicalProfileClaimRef[];
  actions?: JE_DisplayControlAction[];
}

export interface JE_DisplayControlStore {
  displayControlIds: Set<string>;
  displayControls: JE_FQDisplayControlSpec[];
}

export interface JE_FQPredicateParameter {
  id: string;
  value: string;
}

export interface JE_FQPredicateSpec {
  id: string;
  method: string;
  errorMessage?: string;
  parameters: JE_FQPredicateParameter[];
}

export interface JE_PredicateStore {
  predicateIds: Set<string>;
  predicates: JE_FQPredicateSpec[];
}

export const createFQPredicate = (predicateStore: JE_PredicateStore, draftPredicate: JE_FQPredicateSpec) => {
  draftPredicate.id = getNextValidID(draftPredicate.id, predicateStore.predicateIds);
  predicateStore.predicates.push(draftPredicate);
  predicateStore.predicateIds.add(draftPredicate.id.toLowerCase());
  return draftPredicate;
};

export interface JE_FQPredicateGroup {
  id: string;
  userHelpText?: string;
  matchAtLeast?: number;
  predicateReferences: string[];
}

export interface JE_FQPredicateValidationSpec {
  id: string;
  groups: JE_FQPredicateGroup[];
}

export interface JE_PredicateValidationStore {
  pValidationIds: Set<string>;
  pValidations: JE_FQPredicateValidationSpec[];
}

export const createFQPredicateValidation = (
  predicateValidationStore: JE_PredicateValidationStore,
  draftPV: JE_FQPredicateValidationSpec,
) => {
  draftPV.id = getNextValidID(draftPV.id, predicateValidationStore.pValidationIds);
  predicateValidationStore.pValidations.push(draftPV);
  predicateValidationStore.pValidationIds.add(draftPV.id.toLowerCase());
  return draftPV;
};

export interface JE_ClaimsProviderSelectionRef {
  parentNodeId: string;
  addedNodes: Set<string>;
  claimsExchangeLandingStep: JE_FQOrchestrationStepSpec;
  claimsProviderSelectionStep: JE_FQOrchestrationStepSpec;
}

export interface JE_SessionRef {
  sessionId: string;
  sessionProfileId: string;
  sessionFlagClaimId: string;
  sessionInitClaimId: string;
}

export interface JE_ParsingContext {
  buildSettings: BuildSettings;
  claimStore: JE_ClaimStore;
  claimTransformStore: JE_ClaimTransformStore;
  technicalProfileStore: JE_TechnicalProfileStore;
  userJourneyStore: JE_UserJourneyStore;
  predicateStore: JE_PredicateStore;
  predicateValidationStore: JE_PredicateValidationStore;
  clientDefinitionStore: JE_ClientDefinitionStore;
  displayControlStore: JE_DisplayControlStore;
  contentDefinitionStore: JE_ContentDefinitionStore;
  localization: JE_FQLocalizationSpec;
  liveClaims: { [nodeId: string]: Set<string> };
  allBranchVars: string[];
  branchVarMetadata: BranchVarMetadata;
  globalReusables: { [id: string]: any };
  sessions: { [sessionId: string]: JE_SessionRef };
  claimsProviderSelectionMap: { [nodeId: string]: JE_ClaimsProviderSelectionRef };
}

export const createFQDisplayControl = (
  displayControlStore: JE_DisplayControlStore,
  draftDC: JE_FQDisplayControlSpec,
) => {
  let dcId = getNextValidID(draftDC.id, displayControlStore.displayControlIds);
  draftDC.id = dcId;
  displayControlStore.displayControls.push(draftDC);
  displayControlStore.displayControlIds.add(draftDC.id.toLowerCase());
  return draftDC;
};

export const fetchDefaultFQContentDefinitionRef: (parsingCtx: JE_ParsingContext, baseId: string) => string = (
  parsingCtx: JE_ParsingContext,
  baseId: string,
) => {
  if (
    !parsingCtx.globalReusables.defaultContentDefinitions ||
    !parsingCtx.globalReusables.defaultContentDefinitions[baseId]
  ) {
    throw 'Default localization and content definitions are not in use, but a UI-enabled component defined no content definition URL';
  }
  return parsingCtx.globalReusables.defaultContentDefinitions[baseId];
};

export const getOrCreateFQContentDefinition = (
  parsingCtx: JE_ParsingContext,
  draftCD: JE_FQContentDefinitionSpec,
  cdKey: string,
  forceCreate?: boolean,
) => {
  if (!forceCreate) {
    if (
      parsingCtx.contentDefinitionStore.urlMap.hasOwnProperty(draftCD.loadUri) &&
      parsingCtx.contentDefinitionStore.urlMap[draftCD.loadUri].hasOwnProperty(cdKey)
    ) {
      return parsingCtx.contentDefinitionStore.contentDefinitions[
        parsingCtx.contentDefinitionStore.urlMap[draftCD.loadUri][cdKey]
      ];
    }
  }
  draftCD.id = getNextValidID(draftCD.id, parsingCtx.contentDefinitionStore.cdIds);
  parsingCtx.contentDefinitionStore.cdIds.add(draftCD.id.toLowerCase());
  parsingCtx.contentDefinitionStore.contentDefinitions[draftCD.id] = draftCD;
  if (!parsingCtx.contentDefinitionStore.urlMap.hasOwnProperty(draftCD.loadUri)) {
    parsingCtx.contentDefinitionStore.urlMap[draftCD.loadUri] = {};
  }
  parsingCtx.contentDefinitionStore.urlMap[draftCD.loadUri][cdKey] = draftCD.id;
  return draftCD;
};

export const addSupportedLanguageToLocalization = (parsingCtx: JE_ParsingContext, language: string) => {
  if (!parsingCtx.localization.enabled) {
    parsingCtx.localization.enabled = true;
  }
  if (parsingCtx.localization.supportedLanguages.supportedLanguages.length === 0) {
    parsingCtx.localization.supportedLanguages.defaultLanguage = language;
  }
  parsingCtx.localization.supportedLanguages.supportedLanguages.push(language);
};

export const addLocalizationResourceToLocalization = (parsingCtx: JE_ParsingContext, id: string) => {
  parsingCtx.localization.localizedResources[id] = { id: id, localizedStrings: [] };
};

export const createFQLocalizationString = (
  parsingCtx: JE_ParsingContext,
  draftString: JE_FQLocalizedString,
  resourceId: string,
) => {
  if (!parsingCtx.localization.localizedResources.hasOwnProperty(resourceId)) {
    addLocalizationResourceToLocalization(parsingCtx, resourceId);
  }
  parsingCtx.localization.localizedResources[resourceId].localizedStrings.push(draftString);
};

export const createClaimsProviderSelectionReference = (
  parsingCtx: JE_ParsingContext,
  parentNodeId: string,
  selectionStep: JE_FQOrchestrationStepSpec,
  landingStep: JE_FQOrchestrationStepSpec,
) => {
  if (parsingCtx.claimsProviderSelectionMap[parentNodeId]) {
    throw 'Attempt to create duplicate claims provider selection reference in parsing context';
  }
  parsingCtx.claimsProviderSelectionMap[parentNodeId] = {
    parentNodeId: parentNodeId,
    addedNodes: new Set<string>(),
    claimsExchangeLandingStep: landingStep,
    claimsProviderSelectionStep: selectionStep,
  };
};

export const initJEParsingContent: (settings: BuildSettings) => JE_ParsingContext = (settings) => {
  return {
    buildSettings: settings,
    claimStore: { claimIds: new Set<string>(), declaredClaimIds: new Set<string>(), claims: {} },
    claimTransformStore: { claimXformIds: new Set<string>(), claimXforms: {} },
    technicalProfileStore: { tpIds: new Set<string>(), claimsProviders: {}, providerNameIndex: {} },
    userJourneyStore: { journeyIds: new Set<string>(), claimsExchangeIds: new Set<string>(), userJourneys: {} },
    predicateStore: { predicateIds: new Set<string>(), predicates: [] },
    predicateValidationStore: { pValidationIds: new Set<string>(), pValidations: [] },
    displayControlStore: { displayControlIds: new Set<string>(), displayControls: [] },
    clientDefinitionStore: { clientDefinitionIds: new Set<string>(), clientDefinitions: [] },
    contentDefinitionStore: { cdIds: new Set<string>(), urlMap: {}, contentDefinitions: {} },
    localization: {
      enabled: false,
      supportedLanguages: { defaultLanguage: '', mergeBehavior: 'Append', supportedLanguages: [] },
      localizedResources: {},
    },
    globalReusables: {},
    liveClaims: {},
    allBranchVars: [],
    branchVarMetadata: {},
    claimsProviderSelectionMap: {},
    sessions: {},
  };
};

export const getSpecialClaimEffectiveId: (parsingCtx: JE_ParsingContext, claimId: string) => string | null = (
  parsingCtx: JE_ParsingContext,
  claimId: string
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('specialClaims')) {
    return null;
  }
  if (!parsingCtx.globalReusables.specialClaims.hasOwnProperty(claimId)) {
    return null;
  }
  return parsingCtx.globalReusables.specialClaims[claimId];
}

export const getOrCreateSpecialClaim: (parsingCtx: JE_ParsingContext, claimId: string) => string = (
  parsingCtx: JE_ParsingContext,
  claimId: string,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('specialClaims')) {
    parsingCtx.globalReusables.specialClaims = {};
  }
  if (!parsingCtx.globalReusables.specialClaims.hasOwnProperty(claimId)) {
    parsingCtx.globalReusables.specialClaims[claimId] = createFQClaim(parsingCtx.claimStore, {
      id: claimId,
      displayName: constants.specialClaims[claimId].displayName ?? claimId,
      dataType: constants.specialClaims[claimId].dataType,
      userInputType: constants.specialClaims[claimId].userInputType,
    }).id;
  }
  return parsingCtx.globalReusables.specialClaims[claimId];
};

export const getOrCreateStringHandlingBufferClaim: (parsingCtx: JE_ParsingContext, srcClaimId: string) => string = (
  parsingCtx: JE_ParsingContext,
  srcClaimId: string,
) => {
  if (
    parsingCtx.claimStore.claims[srcClaimId].userInputType &&
    parsingCtx.claimStore.claims[srcClaimId].userInputType == 'Password'
  ) {
    // Special handling for password claims to avoid potential risk of leaking passwords into cross-orchestration-step tokens
    return getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.strCmpPwdBuffer.id);
  } else {
    return getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.strCmpBuffer.id);
  }
};

export const createValidationErrorTP: (parsingCtx: JE_ParsingContext) => string = (
  parsingCtx: JE_ParsingContext
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('validationErrorThrowerXform')) {
    parsingCtx.globalReusables.validationErrorThrowerXform = createFQClaimXform(
      parsingCtx.claimTransformStore,
      {
        id: 'ThrowValidationErrorTrigger',
        method: 'AssertBooleanClaimIsEqualToValue',
        inputClaims: [
          { claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.trueBooleanConstant.id), xformClaimType: 'inputClaim' }
        ],
        inputParameters: [
          { id: 'valueToCompareTo', value: 'false', dataType: 'boolean' }
        ]
      }
    ).id
  }
  return createFQTechnicalProfile(
    parsingCtx.technicalProfileStore,
    {
      id: 'ThrowValidationError',
      displayName: 'Throw Validation Error',
      protocol: constants.customPolicySchema.protocols.claimsTransformation,
      inputClaims: [
        { claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.trueBooleanConstant.id), defaultValue: 'true', alwaysUseDefaultValue: true }
      ],
      outputClaims: [
        { claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.trueBooleanConstant.id), defaultValue: 'true', alwaysUseDefaultValue: true }
      ],
      outputClaimXformIds: [
        parsingCtx.globalReusables.validationErrorThrowerXform
      ],
      useTechnicalProfileForSessionManagement: getOrCreateNoopSessionManagementTP(parsingCtx).id
    },
    'Validation Errors'
  ).id;
}

export const getOrCreateUserForeignIdentityReadXform: (parsingCtx: JE_ParsingContext) => string = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('foreignIDReadXform')) {
    parsingCtx.globalReusables.foreignIDReadXform = createFQClaimXform(parsingCtx.claimTransformStore, {
      id: 'CreateForeignIDCollectionForRead',
      method: 'AddItemToUserIdentityCollection',
      inputClaims: [
        { claimId: 'foreignId', xformClaimType: 'item' },
        {
          claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.emptyUserIdentities.id),
          xformClaimType: 'collection',
        },
      ],
      outputClaims: [
        {
          claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.userIdentitiesForRead.id),
          xformClaimType: 'collection',
        },
      ],
    }).id;
  }
  return parsingCtx.globalReusables.foreignIDReadXform;
};

export const getOrCreateUserForeignIdentityCreationXforms: (parsingCtx: JE_ParsingContext) => string[] = (
  parsingCtx: JE_ParsingContext,
) => {
  let xformIds: string[] = [];
  if (!parsingCtx.globalReusables.hasOwnProperty('foreignIDCreationXforms')) {
    parsingCtx.globalReusables.foreignIDCreationXforms = {};
  }
  if (!parsingCtx.globalReusables.foreignIDCreationXforms.hasOwnProperty('createRandomUPNUserName')) {
    parsingCtx.globalReusables.foreignIDCreationXforms.createRandomUPNUserName = createFQClaimXform(
      parsingCtx.claimTransformStore,
      {
        id: 'CreateRandomUPNUserName',
        method: 'CreateRandomString',
        inputParameters: [{ id: 'randomGeneratorType', dataType: 'string', value: 'GUID' }],
        outputClaims: [
          {
            claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.upnUserName.id),
            xformClaimType: 'outputClaim',
          },
        ],
      },
    ).id;
  }
  xformIds.push(parsingCtx.globalReusables.foreignIDCreationXforms.createRandomUPNUserName);
  if (!parsingCtx.globalReusables.foreignIDCreationXforms.hasOwnProperty('createUserPrincipalName')) {
    parsingCtx.globalReusables.foreignIDCreationXforms.createUserPrincipalName = createFQClaimXform(
      parsingCtx.claimTransformStore,
      {
        id: 'CreateUserPrincipalName',
        method: 'FormatStringClaim',
        inputClaims: [
          {
            claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.upnUserName.id),
            xformClaimType: 'inputClaim',
          },
        ],
        inputParameters: [{ id: 'stringFormat', dataType: 'string', value: 'cpim_{0}@{RelyingPartyTenantId}' }],
        outputClaims: [
          {
            claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.userPrincipalName.id),
            xformClaimType: 'outputClaim',
          },
        ],
      },
    ).id;
  }
  xformIds.push(parsingCtx.globalReusables.foreignIDCreationXforms.createUserPrincipalName);
  if (!parsingCtx.globalReusables.foreignIDCreationXforms.hasOwnProperty('createUserForeignID')) {
    parsingCtx.globalReusables.foreignIDCreationXforms.createUserForeignID = createFQClaimXform(
      parsingCtx.claimTransformStore,
      {
        id: 'CreateUserForeignID',
        method: 'CreateUserIdentity',
        inputClaims: [
          {
            claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.issuerUserId.id),
            xformClaimType: 'issuerUserId',
          },
          {
            claimId: getOrCreateSpecialClaim(parsingCtx, constants.specialClaims.identityProvider.id),
            xformClaimType: 'issuer',
          },
        ],
        outputClaims: [{ claimId: 'foreignId', xformClaimType: 'userIdentity' }],
      },
    ).id;
  }
  xformIds.push(parsingCtx.globalReusables.foreignIDCreationXforms.createUserForeignID);
  return xformIds;
};

export const getOrCreateNonemptyStringPredicateValidation: (parsingCtx: JE_ParsingContext) => string = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('nonemptyStringPredicateValidation')) {
    let nePredicateId = createFQPredicate(parsingCtx.predicateStore, {
      id: 'jexg_NonemptyPredicate',
      method: 'MatchesRegex',
      parameters: [{ id: 'RegularExpression', value: '^.+$' }],
    }).id;
    parsingCtx.globalReusables.nonemptyStringPredicateValidation = createFQPredicateValidation(
      parsingCtx.predicateValidationStore,
      {
        id: 'jexg_NonemptyPredicateValidation',
        groups: [{ id: 'jexg_nonempty_p_val_grp', predicateReferences: [nePredicateId] }],
      },
    ).id;
  }
  return parsingCtx.globalReusables.nonemptyStringPredicateValidation;
};

export const getOrCreateAADAccessTokenClaim: (parsingCtx: JE_ParsingContext, clientIdContainer: string, clientSecretContainer: string, index?: number) => string = (
  parsingCtx: JE_ParsingContext,
  clientIdContainer: string,
  clientSecretContainer: string,
  index?: number
) => {
  let tokenKey = `${clientIdContainer}_${clientSecretContainer}`
  if (!parsingCtx.globalReusables.hasOwnProperty('aadAccessTokenClaims')) {
    parsingCtx.globalReusables.aadAccessTokenClaims = {};
  }
  if (!Object.keys(parsingCtx.globalReusables.aadAccessTokenClaims).includes(tokenKey)) {
    parsingCtx.globalReusables.aadAccessTokenClaims[tokenKey] = createFQClaim(
      parsingCtx.claimStore,
      {
        id: `AADAccessToken_${index!}`,
        displayName: `AAD Access Token ${index!}`,
        dataType: 'string'
      }
    ).id
  }
  return parsingCtx.globalReusables.aadAccessTokenClaims[tokenKey];
}

export const getOrCreateStringWriteKeyNonemptyBufferClaim: (parsingCtx: JE_ParsingContext) => string = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('stringWriteKeyNonemptyBufferClaim')) {
    parsingCtx.globalReusables.stringWriteKeyNonemptyBufferClaim = createFQClaim(parsingCtx.claimStore, {
      id: `jexg_StringWriteKeyNonemptyBuffer`,
      displayName: 'AAD Write Key String Nonempty Buffer',
      dataType: 'string',
      predicateValidationReference: getOrCreateNonemptyStringPredicateValidation(parsingCtx),
    }).id;
  }
  return parsingCtx.globalReusables.stringWriteKeyNonemptyBufferClaim;
};

export const getOrCreateSSCTransform: (parsingCtx: JE_ParsingContext, ssc: string, outputClaimId?: string) => string = (
  parsingCtx: JE_ParsingContext,
  ssc: string,
  outputClaimId?: string,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sscTransforms')) {
    parsingCtx.globalReusables.sscTransforms = {};
  }
  let xformId = '';
  if (outputClaimId || !parsingCtx.globalReusables.sscTransforms[ssc]) {
    switch (ssc) {
      case constants.sessionContexts.specials.CurrentDateTime.specialId:
        xformId = createFQClaimXform(parsingCtx.claimTransformStore, {
          id: `Get_SpecialSessionContext_CurrentDateTime_${outputClaimId ? outputClaimId : 'Buffer'}`,
          method: 'GetCurrentDateTime',
          outputClaims: [
            {
              claimId: outputClaimId
                ? outputClaimId
                : getOrCreateSpecialClaim(
                  parsingCtx,
                  constants.sessionContexts.specials[ssc as keyof typeof constants.sessionContexts.specials].claimId,
                ),
              xformClaimType: 'currentDateTime',
            },
          ],
        }).id;
        if (!outputClaimId) {
          parsingCtx.globalReusables.sscTransforms[ssc] = xformId;
        } else {
          return xformId;
        }
        break;
      default:
        throw 'Unknown SSC type in transform generation';
    }
  }
  return parsingCtx.globalReusables.sscTransforms[ssc];
};

export const getOrCreateNoopSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-Noop')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-Noop'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-Noop',
        displayName: 'Noop Session Management Provider',
        protocol: constants.customPolicySchema.protocols.noopSSO,
      },
      'Session Management',
    );
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-Noop'];
};

export const getOrCreateSocialSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-Social')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-Social'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-Social',
        displayName: 'Social Session Management Provider',
        protocol: constants.customPolicySchema.protocols.externalLoginSSO,
        metadata: [{ key: 'AlwaysFetchClaimsFromProvider', value: 'true' }],
        persistedClaims: [{ claimId: 'foreignId' }],
      },
      'Session Management',
    );
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-Social'];
};

export const getOrCreateSamlIdPSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-Saml-Idp')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-Saml-Idp'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-Saml-Idp',
        displayName: 'SAML IdP Session Management Provider',
        protocol: constants.customPolicySchema.protocols.samlSSO,
        metadata: [
          { key: 'IncludeSessionIndex', value: 'false' },
          { key: 'RegisterServiceProviders', value: 'false' }
        ],
      },
      'Session Management'
    )
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-Saml-Idp'];
}

export const getOrCreateDefaultSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-Default')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-Default'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-Default',
        displayName: 'Default Session Management Provider',
        protocol: constants.customPolicySchema.protocols.defaultSSO,
      },
      'Session Management',
    );
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-Default'];
};

export const addClaimToDefaultSessionManagementTP = (
  parsingCtx: JE_ParsingContext,
  claimId: string,
  defaultValue?: string,
  alwaysUseDefaultValue?: boolean,
) => {
  let dSMTP = getOrCreateDefaultSessionManagementTP(parsingCtx);
  if (!dSMTP.persistedClaims) {
    dSMTP.persistedClaims = [];
  }
  if (
    dSMTP.persistedClaims!.filter((claimRef: JE_technicalProfileClaimRef) => {
      return claimRef.claimId === claimId;
    }).length === 0
  ) {
    dSMTP.persistedClaims.push({
      claimId: claimId,
      defaultValue: defaultValue,
      alwaysUseDefaultValue: alwaysUseDefaultValue,
    });
  }
};

export const getOrCreateJWTIssuerSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-jwt-issuer')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-jwt-issuer'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-jwt-issuer',
        displayName: 'OAuth Session Management Provider',
        protocol: constants.customPolicySchema.protocols.oAuthSSO,
      },
      'Session Management',
    );
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-jwt-issuer'];
};

const getOrCreateSAMLIssuerSessionManagementTP: (parsingCtx: JE_ParsingContext) => JE_FQTechnicalProfileSpec = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('sessionManagementTPs')) {
    parsingCtx.globalReusables.sessionManagementTPs = {};
  }
  if (!parsingCtx.globalReusables.sessionManagementTPs.hasOwnProperty('SM-saml-issuer')) {
    parsingCtx.globalReusables.sessionManagementTPs['SM-saml-issuer'] = createFQTechnicalProfile(
      parsingCtx.technicalProfileStore,
      {
        id: 'SM-saml-issuer',
        displayName: 'SAML2 Session Management Provider',
        protocol: constants.customPolicySchema.protocols.samlSSO,
        metadata: [{ key: 'RegisterServiceProviders', value: 'true' }],
      },
      'Session Management',
    );
  }
  return parsingCtx.globalReusables.sessionManagementTPs['SM-saml-issuer'];
};

export const getOrCreateTokenIssuerTechnicalProfile: (
  parsingCtx: JE_ParsingContext,
  type: string,
  issuerUri?: string,
) => string = (parsingCtx: JE_ParsingContext, type: string, issuerUri?: string) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('tokenIssuerTPs')) {
    parsingCtx.globalReusables.tokenIssuerTPs = {};
  }
  if (!parsingCtx.globalReusables.tokenIssuerTPs.hasOwnProperty(type)) {
    // TODO: Make this properly adaptive to user settings and generate correct profiles for other issuers and configurations.
    if (type === constants.customPolicySchema.tokenIssuerTypes.oidc.type) {
      parsingCtx.globalReusables.tokenIssuerTPs[type] = createFQTechnicalProfile(
        parsingCtx.technicalProfileStore,
        {
          id: 'jwtIssuer',
          displayName: 'JWT Issuer',
          protocol: constants.customPolicySchema.protocols.openIdConnect,
          outputTokenFormat: constants.customPolicySchema.tokenIssuerTypes.oidc.tokenFormat,
          metadata: [
            { key: 'client_id', value: '{service:te}' },
            { key: 'issuer_refresh_token_user_identity_claim_type', value: 'objectId' },
            { key: 'SendTokenResponseBodyWithJsonNumbers', value: 'true' },
          ],
          cryptographicKeys: [
            {
              id: 'issuer_secret',
              storageReferenceId:
                extractSettingFieldValue('tokenSigningKeyContainer', parsingCtx.buildSettings) ?? getTenantSettings().tokenSigningKeyContainer,
            },
            {
              id: 'issuer_refresh_token_key',
              storageReferenceId:
                extractSettingFieldValue('tokenEncryptionKeyContainer', parsingCtx.buildSettings) ??
                getTenantSettings().tokenEncryptionKeyContainer,
            },
          ],
          useTechnicalProfileForSessionManagement: getOrCreateJWTIssuerSessionManagementTP(parsingCtx).id,
        },
        'Token Issuers',
      ).id;
    } else if (type === constants.customPolicySchema.tokenIssuerTypes.saml.type) {
      parsingCtx.globalReusables.tokenIssuerTPs[type] = {};
    }
  }
  if (type == constants.customPolicySchema.tokenIssuerTypes.oidc.type) {
    return parsingCtx.globalReusables.tokenIssuerTPs[type];
  } else if (type == constants.customPolicySchema.tokenIssuerTypes.saml.type) {
    if (!issuerUri) {
      throw 'An issuer URI is required for a SAML token issuer';
    } else {
      if (!parsingCtx.globalReusables.tokenIssuerTPs[type].hasOwnProperty(issuerUri)) {
        let samlCert = extractSettingFieldValue('SAMLCert', parsingCtx.buildSettings);
        if (!samlCert) {
          throw 'No SAML app cert reference present in tenant settings; required for this policy';
        }
        parsingCtx.globalReusables.tokenIssuerTPs[type][issuerUri] = createFQTechnicalProfile(
          parsingCtx.technicalProfileStore,
          {
            id: 'samlAssertionIssuer',
            displayName: 'SAML2 Assertion Issuer',
            protocol: constants.customPolicySchema.protocols.saml2,
            outputTokenFormat: constants.customPolicySchema.tokenIssuerTypes.saml.tokenFormat,
            metadata: [{ key: 'IssuerUri', value: issuerUri }],
            cryptographicKeys: [
              { id: 'SamlAssertionSigning', storageReferenceId: samlCert },
              { id: 'SamlMessageSigning', storageReferenceId: samlCert },
            ],
            useTechnicalProfileForSessionManagement: getOrCreateSAMLIssuerSessionManagementTP(parsingCtx).id,
          },
          'Token Issuers',
        ).id;
      }
      return parsingCtx.globalReusables.tokenIssuerTPs[type][issuerUri];
    }
  }
};

export const getOrCreateTrueBooleanConstant = (parsingCtx: JE_ParsingContext) => {
  if (parsingCtx.globalReusables.hasOwnProperty('booleanConstantTrue')) {
    return parsingCtx.globalReusables.booleanConstantTrue as string;
  } else {
    let bct = createFQClaim(parsingCtx.claimStore, {
      id: `jexg_BooleanCopyDummy`,
      dataType: 'boolean',
      displayName: 'Boolean Copy Dummy',
    });
    parsingCtx.globalReusables.booleanConstantTrue = bct.id;
    return bct.id;
  }
};

const getOrCreateWebAuthnContentDefinition: (
  parsingCtx: JE_ParsingContext,
  pcgrKey: string,
  id: string,
  uri: string,
) => string = (parsingCtx: JE_ParsingContext, pcgrKey: string, id: string, uri: string) => {
  if (!parsingCtx.globalReusables.hasOwnProperty(pcgrKey)) {
    let wacd = {
      id: id,
      loadUri: uri,
      recoveryUri: '~/common/default_page_error.html',
      dataUri: 'urn:com:microsoft:aad:b2c:elements:contract:selfasserted:2.1.7',
      metadata: [{ key: 'DisplayName', value: 'WebAuthn' }],
    };
    getOrCreateFQContentDefinition(parsingCtx, wacd, id, true);
    parsingCtx.globalReusables[pcgrKey] = wacd.id;
  }
  return parsingCtx.globalReusables[pcgrKey];
};

export const getOrCreateWebAuthnRegistrationContentDefinition = (parsingCtx: JE_ParsingContext) => {
  return getOrCreateWebAuthnContentDefinition(
    parsingCtx,
    'webAuthnRegistrationContentDefinition',
    'api.webauthnreg',
    settings.webAuthnRegistrationContentDefinitionUri,
  );
};

export const getOrCreateWebAuthnAuthenticationContentDefinition = (parsingCtx: JE_ParsingContext) => {
  return getOrCreateWebAuthnContentDefinition(
    parsingCtx,
    'webAuthnAuthenaticationContentDefinition',
    'api.webauthnauth',
    settings.webAuthnAuthenticationContentDefinitionUri,
  );
};

interface JE_BCFBuffers {
  orBuf: string;
  andBuf: string;
  nandBuf: string;
  xorBuf: string;
}

interface JE_BCFTransforms {
  nandXform: string;
  xorXform: string;
}

interface JE_BooleanComparisonFramework {
  bufferClaims: JE_BCFBuffers;
  xforms: JE_BCFTransforms;
}

export const getOrCreateBooleanComparisonFramework: (parsingCtx: JE_ParsingContext) => JE_BooleanComparisonFramework = (
  parsingCtx: JE_ParsingContext,
) => {
  if (!parsingCtx.globalReusables.hasOwnProperty('booleanComparisonFramework')) {
    let buffers: JE_BCFBuffers = {
      orBuf: createFQClaim(parsingCtx.claimStore, {
        id: `jexg_BooleanComparisonOrBuffer`,
        displayName: 'Boolean XOR Comparison OR Buffer',
        dataType: 'boolean',
      }).id,
      andBuf: createFQClaim(parsingCtx.claimStore, {
        id: `jexg_BooleanComparisonAndBuffer`,
        displayName: 'Boolean XOR Comparison AND Buffer',
        dataType: 'boolean',
      }).id,
      nandBuf: createFQClaim(parsingCtx.claimStore, {
        id: `jexg_BooleanComparisonNandBuffer`,
        displayName: 'Boolean XOR Comparison NAND Buffer',
        dataType: 'boolean',
      }).id,
      xorBuf: createFQClaim(parsingCtx.claimStore, {
        id: `jexg_BooleanComparisonXorBuffer`,
        displayName: 'Boolean XOR Comparison XOR Buffer',
        dataType: 'boolean',
      }).id,
    };
    parsingCtx.globalReusables.booleanComparisonFramework = {
      bufferClaims: buffers,
      xforms: {
        nandXform: createFQClaimXform(parsingCtx.claimTransformStore, {
          id: 'jexg_BooleanComparisonNandStep',
          method: 'NotClaims',
          inputClaims: [{ claimId: buffers.andBuf, xformClaimType: 'inputClaim' }],
          outputClaims: [{ claimId: buffers.nandBuf, xformClaimType: 'outputClaim' }],
        }).id,
        xorXform: createFQClaimXform(parsingCtx.claimTransformStore, {
          id: 'jexg_BooleanComparisonXorStep',
          method: 'AndClaims',
          inputClaims: [
            { claimId: buffers.nandBuf, xformClaimType: 'inputClaim1' },
            { claimId: buffers.orBuf, xformClaimType: 'inputClaim2' },
          ],
          outputClaims: [{ claimId: buffers.xorBuf, xformClaimType: 'outputClaim' }],
        }).id,
      },
    };
  }
  return parsingCtx.globalReusables.booleanComparisonFramework as JE_BooleanComparisonFramework;
};

type JE_XMLElementAttributes = {
  [index: string]: string;
};

const createXMLNode = (
  xmlDoc: XMLDocument,
  name: string,
  attributes: any,
  innerText: string | null = null,
  parent: Element | null = null,
) => {
  // Need to use createElementNS to avoid some browser-related issues regarding xmlns attributes
  let newNode = xmlDoc.createElementNS(constants.customPolicySchema.xmlSchemas.tfp, name);
  Object.keys(attributes).forEach((attrName: any) => {
    newNode.setAttribute(attrName, attributes[attrName]);
  });
  if (innerText) {
    newNode.textContent = innerText;
  }
  if (parent) {
    parent.appendChild(newNode);
  }
  return newNode;
};

const generateTPClaimRefFromFQSpecs = (
  xmlDoc: XMLDocument,
  sectionType: string,
  claimType: string,
  claimRefs: JE_technicalProfileClaimRef[] | undefined,
  technicalProfile: Element,
) => {
  if (claimRefs && claimRefs.length > 0) {
    let sectionClaims = createXMLNode(xmlDoc, sectionType, {}, null, technicalProfile);
    claimRefs.forEach((claimRef: JE_technicalProfileClaimRef) => {
      let claimRefAttrs: { [key: string]: string } = {};
      if (claimRef.claimId !== undefined) {
        claimRefAttrs[xmlStrings.claimTypeReferenceId] = claimRef.claimId;
      }
      if (claimRef.displayControlId !== undefined) {
        claimRefAttrs[xmlStrings.displayControlReferenceId] = claimRef.displayControlId;
      }
      if (claimRef.defaultValue !== undefined) {
        claimRefAttrs[xmlStrings.defaultValue] = claimRef.defaultValue;
      }
      if (claimRef.alwaysUseDefaultValue !== undefined) {
        claimRefAttrs[xmlStrings.alwaysUseDefaultValue] = claimRef.alwaysUseDefaultValue.toString();
      }
      if (claimRef.partnerClaimType !== undefined) {
        claimRefAttrs[xmlStrings.partnerClaimType] = claimRef.partnerClaimType;
      }
      if (claimRef.controlClaimType !== undefined) {
        claimRefAttrs[xmlStrings.controlClaimType] = claimRef.controlClaimType;
      }
      if (claimRef.required !== undefined) {
        claimRefAttrs[xmlStrings.required] = claimRef.required.toString();
      }
      createXMLNode(xmlDoc, claimType, claimRefAttrs, null, sectionClaims);
    });
  }
};

const generatePreconditionListXML = (
  xmlDoc: XMLDocument,
  preconditionList: JE_FQPreconditionSpec[] | undefined,
  parentElement: Element,
) => {
  if (preconditionList && preconditionList.length > 0) {
    let preconditions = createXMLNode(xmlDoc, xmlStrings.preconditions, {}, null, parentElement);
    preconditionList.forEach((pc: JE_FQPreconditionSpec) => {
      let precondition = createXMLNode(
        xmlDoc,
        xmlStrings.precondition,
        { [xmlStrings.type]: pc.type, [xmlStrings.executeActionsIf]: pc.executeActionsIf.toString() },
        null,
        preconditions,
      );
      pc.values.forEach((val: string) => {
        createXMLNode(xmlDoc, xmlStrings.value, {}, val, precondition);
      });
      createXMLNode(xmlDoc, xmlStrings.action, {}, pc.action, precondition);
    });
  }
};

const generateTFPRootNode = (
  xmlDoc: XMLDocument,
  tenantUri: string,
  policyId: string,
  journeyInsightsEnabled?: boolean,
  basePolicyTenant?: string,
  basePolicyId?: string,
) => {
  let tfpRoot = xmlDoc.createElementNS(constants.customPolicySchema.xmlSchemas.tfp, xmlStrings.trustFrameworkPolicy);
  let tfpAttrs: { [index: string]: string } = {
    'xmlns:xsi': constants.customPolicySchema.xmlSchemas.xsi,
    'xmlns:xsd': constants.customPolicySchema.xmlSchemas.xsd,
    [xmlStrings.policySchemaVersion]: '0.3.0.0',
    [xmlStrings.tenantId]: tenantUri,
    [xmlStrings.policyId]: policyId,
    [xmlStrings.publicPolicyUri]: `http://${tenantUri}/${policyId}`,
  };
  if (basePolicyTenant && basePolicyId && journeyInsightsEnabled) {
    tfpAttrs[xmlStrings.deploymentMode] = 'Development';
    tfpAttrs[xmlStrings.userJourneyRecorderEndpoint] = 'urn:journeyrecorder:applicationinsights';
  }
  Object.keys(tfpAttrs).forEach((attrName: string) => tfpRoot.setAttribute(attrName, tfpAttrs[attrName]));
  xmlDoc.appendChild(tfpRoot);
  if (basePolicyTenant && basePolicyId) {
    let basePolicy = createXMLNode(xmlDoc, xmlStrings.basePolicy, {}, null, tfpRoot);
    createXMLNode(xmlDoc, xmlStrings.tenantId, {}, tenantUri, basePolicy);
    createXMLNode(xmlDoc, xmlStrings.policyId, {}, basePolicyId, basePolicy);
  }
  return tfpRoot;
};

export const generateRelyingPartyXML = (
  relyingPartySpec: JE_FQRelyingPartySpec,
  relyingPartyPolicyName: string,
  tenantUri: string,
  policyId: string,
  enableJourneyInsights: boolean
) => {
  let xmlDoc = document.implementation.createDocument(null, null);
  let tfpRoot = generateTFPRootNode(xmlDoc, tenantUri, relyingPartyPolicyName, enableJourneyInsights, tenantUri, policyId);
  let relyingParty = createXMLNode(xmlDoc, xmlStrings.relyingParty, {}, null, tfpRoot);
  createXMLNode(
    xmlDoc,
    xmlStrings.defaultUserJourney,
    { [xmlStrings.referenceId]: relyingPartySpec.defaultUserJourney },
    null,
    relyingParty,
  );
  if (relyingPartySpec.endpoints && relyingPartySpec.endpoints.length > 0) {
    let endpoints = createXMLNode(xmlDoc, xmlStrings.endpoints, {}, null, relyingParty);
    relyingPartySpec.endpoints.forEach((endPt) => {
      createXMLNode(
        xmlDoc,
        xmlStrings.endpoint,
        { [xmlStrings.id]: endPt.id, [xmlStrings.userJourneyReferenceId]: endPt.userJourneyReferenceId },
        null,
        endpoints,
      );
    });
  }
  if (relyingPartySpec.userJourneyBehaviors) {
    let userJourneyBehaviors = createXMLNode(xmlDoc, xmlStrings.userJourneyBehaviors, {}, null, relyingParty);
    createXMLNode(
      xmlDoc,
      xmlStrings.singleSignOn,
      { [xmlStrings.scope]: relyingPartySpec.userJourneyBehaviors.singleSignOn.scope },
      null,
      userJourneyBehaviors,
    );
    createXMLNode(
      xmlDoc,
      xmlStrings.sessionExpiryType,
      {},
      relyingPartySpec.userJourneyBehaviors.sessionExpiryType,
      userJourneyBehaviors,
    );
    createXMLNode(
      xmlDoc,
      xmlStrings.sessionExpiryInSeconds,
      {},
      relyingPartySpec.userJourneyBehaviors.sessionExpiryInSeconds.toString(),
      userJourneyBehaviors,
    );
    if (relyingPartySpec.userJourneyBehaviors.journeyInsights) {
      let ji = relyingPartySpec.userJourneyBehaviors.journeyInsights;
      createXMLNode(
        xmlDoc,
        xmlStrings.journeyInsights,
        {
          [xmlStrings.telemetryEngine]: ji.telemetryEngine,
          [xmlStrings.instrumentationKey]: ji.instrumentationKey,
          [xmlStrings.developerMode]: ji.developerMode.toString(),
          [xmlStrings.clientEnabled]: ji.clientEnabled.toString(),
          [xmlStrings.serverEnabled]: ji.serverEnabled.toString(),
          [xmlStrings.telemetryVersion]: ji.telemetryVersion,
        },
        null,
        userJourneyBehaviors,
      );
    }
    if (relyingPartySpec.userJourneyBehaviors.scriptExecution) {
      createXMLNode(
        xmlDoc,
        xmlStrings.scriptExecution,
        {},
        relyingPartySpec.userJourneyBehaviors.scriptExecution,
        userJourneyBehaviors,
      );
    }
  }
  generateTechnicalProfileXMLFromFQSpec(xmlDoc, relyingPartySpec.technicalProfile, relyingParty);
  return new XMLSerializer().serializeToString(xmlDoc.documentElement);
};

const generateTechnicalProfileXMLFromFQSpec = (
  xmlDoc: XMLDocument,
  tp: JE_FQTechnicalProfileSpec,
  parentNode: Element,
) => {
  let technicalProfile = createXMLNode(
    xmlDoc,
    xmlStrings.technicalProfile,
    { [xmlStrings.id]: tp.id },
    null,
    parentNode,
  );
  createXMLNode(xmlDoc, xmlStrings.displayName, {}, tp.displayName, technicalProfile);
  if (tp.protocol) {
    let protocolAttrs: JE_XMLElementAttributes = { [xmlStrings.name]: tp.protocol.name };
    if (tp.protocol.handler) {
      protocolAttrs[xmlStrings.handler] = tp.protocol.handler;
    }
    createXMLNode(xmlDoc, xmlStrings.protocol, protocolAttrs, null, technicalProfile);
  }
  if (tp.inputTokenFormat) {
    createXMLNode(xmlDoc, xmlStrings.inputTokenFormat, {}, tp.inputTokenFormat, technicalProfile);
  }
  if (tp.outputTokenFormat) {
    createXMLNode(xmlDoc, xmlStrings.outputTokenFormat, {}, tp.outputTokenFormat, technicalProfile);
  }
  if (tp.metadata && tp.metadata.length > 0) {
    let metadata = createXMLNode(xmlDoc, xmlStrings.metadata, {}, null, technicalProfile);
    tp.metadata.forEach((md: JE_FQMetadataItemSpec) => {
      createXMLNode(xmlDoc, xmlStrings.item, { [xmlStrings.key]: md.key }, md.value, metadata);
    });
  }
  if (tp.cryptographicKeys && tp.cryptographicKeys.length > 0) {
    let cryptographicKeys = createXMLNode(xmlDoc, xmlStrings.cryptographicKeys, {}, null, technicalProfile);
    tp.cryptographicKeys.forEach((ck: JE_CryptographicKeyRef) => {
      createXMLNode(
        xmlDoc,
        xmlStrings.key,
        { [xmlStrings.id]: ck.id, [xmlStrings.storageReferenceId]: ck.storageReferenceId },
        null,
        cryptographicKeys,
      );
    });
  }
  if (tp.includeInSso !== undefined) {
    createXMLNode(xmlDoc, xmlStrings.includeInSso, {}, tp.includeInSso.toString(), technicalProfile);
  }
  if (tp.inputClaimXformIds && tp.inputClaimXformIds.length > 0) {
    let inputClaimsTransformations = createXMLNode(
      xmlDoc,
      xmlStrings.inputClaimsTransformations,
      {},
      null,
      technicalProfile,
    );
    tp.inputClaimXformIds.forEach((xformId: string) => {
      createXMLNode(
        xmlDoc,
        xmlStrings.inputClaimsTransformation,
        { [xmlStrings.referenceId]: xformId },
        null,
        inputClaimsTransformations,
      );
    });
  }
  generateTPClaimRefFromFQSpecs(
    xmlDoc,
    xmlStrings.inputClaims,
    xmlStrings.inputClaim,
    tp.inputClaims,
    technicalProfile,
  );
  generateTPClaimRefFromFQSpecs(
    xmlDoc,
    xmlStrings.displayClaims,
    xmlStrings.displayClaim,
    tp.displayClaims,
    technicalProfile,
  );
  generateTPClaimRefFromFQSpecs(
    xmlDoc,
    xmlStrings.persistedClaims,
    xmlStrings.persistedClaim,
    tp.persistedClaims,
    technicalProfile,
  );
  generateTPClaimRefFromFQSpecs(
    xmlDoc,
    xmlStrings.outputClaims,
    xmlStrings.outputClaim,
    tp.outputClaims,
    technicalProfile,
  );
  if (tp.outputClaimXformIds && tp.outputClaimXformIds.length > 0) {
    let outputClaimsTransformations = createXMLNode(
      xmlDoc,
      xmlStrings.outputClaimsTransformations,
      {},
      null,
      technicalProfile,
    );
    tp.outputClaimXformIds.forEach((xformId: string) => {
      createXMLNode(
        xmlDoc,
        xmlStrings.outputClaimsTransformation,
        { [xmlStrings.referenceId]: xformId },
        null,
        outputClaimsTransformations,
      );
    });
  }
  if (tp.validationTechnicalProfiles && tp.validationTechnicalProfiles.length > 0) {
    let validationTechnicalProfiles = createXMLNode(
      xmlDoc,
      xmlStrings.validationTechnicalProfiles,
      {},
      null,
      technicalProfile,
    );
    tp.validationTechnicalProfiles.forEach((vtp: JE_ValidationTechnicalProfileRef) => {
      let vtpAttrs: JE_XMLElementAttributes = { [xmlStrings.referenceId]: vtp.profileId };
      if (vtp.continueOnError !== undefined) {
        vtpAttrs[xmlStrings.continueOnError] = vtp.continueOnError.toString();
      }
      if (vtp.continueOnSuccess !== undefined) {
        vtpAttrs[xmlStrings.continueOnSuccess] = vtp.continueOnSuccess.toString();
      }
      let validationTechnicalProfile = createXMLNode(
        xmlDoc,
        xmlStrings.validationTechnicalProfile,
        vtpAttrs,
        null,
        validationTechnicalProfiles,
      );
      generatePreconditionListXML(xmlDoc, vtp.preconditions, validationTechnicalProfile);
    });
  }
  if (tp.includeTechnicalProfile) {
    createXMLNode(
      xmlDoc,
      xmlStrings.includeTechnicalProfile,
      { [xmlStrings.referenceId]: tp.includeTechnicalProfile },
      null,
      technicalProfile,
    );
  }
  if (tp.useTechnicalProfileForSessionManagement) {
    createXMLNode(
      xmlDoc,
      xmlStrings.useTechnicalProfileForSessionManagement,
      { [xmlStrings.referenceId]: tp.useTechnicalProfileForSessionManagement },
      null,
      technicalProfile,
    );
  }
  if (tp.subjectNamingInfo) {
    let sniAttrs: JE_XMLElementAttributes = { [xmlStrings.claimType]: tp.subjectNamingInfo.claimType };
    if (tp.subjectNamingInfo.format) {
      sniAttrs[xmlStrings.format] = tp.subjectNamingInfo.format;
    }
    if (tp.subjectNamingInfo.excludeAsClaim !== undefined) {
      sniAttrs[xmlStrings.excludeAsClaim] = tp.subjectNamingInfo.excludeAsClaim.toString();
    }
    createXMLNode(xmlDoc, xmlStrings.subjectNamingInfo, sniAttrs, null, technicalProfile);
  }
};

export const generateXMLFromFQSpecs = (parsingCtx: JE_ParsingContext, tenantUri: string, policyId: string) => {
  let claims = parsingCtx.claimStore;
  let claimXforms = parsingCtx.claimTransformStore;
  let predicateStore = parsingCtx.predicateStore;
  let pValidationStore = parsingCtx.predicateValidationStore;
  let technicalProfileStore = parsingCtx.technicalProfileStore;
  let userJourneyStore = parsingCtx.userJourneyStore;
  let clientDefinitionStore = parsingCtx.clientDefinitionStore;
  let contentDefinitionStore = parsingCtx.contentDefinitionStore;
  let displayControlStore = parsingCtx.displayControlStore;
  let localizationStore = parsingCtx.localization;

  // Set up document and TFP header section.
  let xmlDoc = document.implementation.createDocument(null, null);

  let tfpRoot = generateTFPRootNode(xmlDoc, tenantUri, policyId);

  // Generate claims, claim transforms, etc.

  if (
    (claims.claims && Object.keys(claims.claims).length > 0) ||
    (claimXforms.claimXforms && Object.keys(claimXforms.claimXforms).length > 0)
  ) {
    let buildingBlocks = createXMLNode(xmlDoc, xmlStrings.buildingBlocks, {}, null, tfpRoot);

    // Make claims.
    if (claims.claims && Object.keys(claims.claims).length > 0) {
      let claimsSchema = createXMLNode(xmlDoc, xmlStrings.claimsSchema, {}, null, buildingBlocks);
      Object.keys(claims.claims).forEach((claimId: string) => {
        let claim: JE_FQClaimSpec = claims.claims[claimId];
        let claimType = createXMLNode(xmlDoc, xmlStrings.claimType, { Id: claim.id }, null, claimsSchema);
        createXMLNode(xmlDoc, xmlStrings.displayName, {}, claim.displayName, claimType);
        createXMLNode(xmlDoc, xmlStrings.dataType, {}, claim.dataType, claimType);
        if (claim.mask) {
          let maskAttrs: JE_XMLElementAttributes = { [xmlStrings.type]: claim.mask.type };
          if (claim.mask.type === 'Regex') {
            maskAttrs[xmlStrings.regex] = claim.mask.regex!;
          }
          createXMLNode(xmlDoc, xmlStrings.mask, maskAttrs, claim.mask.value, claimType);
        }
        if (claim.userInputType) {
          createXMLNode(xmlDoc, xmlStrings.userInputType, {}, claim.userInputType, claimType);
        }
        if (claim.restriction) {
          let restriction = createXMLNode(xmlDoc, xmlStrings.restriction, {}, null, claimType);
          if (claim.enumeration) {
            claim.enumeration!.forEach((enumOpt: JE_FQEnumerationSpec) => {
              createXMLNode(
                xmlDoc,
                xmlStrings.enumeration,
                {
                  [xmlStrings.text]: enumOpt.text,
                  [xmlStrings.value]: enumOpt.value,
                  [xmlStrings.selectByDefault]: enumOpt.selectByDefault,
                },
                null,
                restriction,
              );
            });
          } else if (claim.pattern) {
            createXMLNode(
              xmlDoc,
              xmlStrings.pattern,
              {
                [xmlStrings.regularExpression]: claim.pattern.regex,
                [xmlStrings.helpText]: claim.pattern.helpText,
              },
              null,
              restriction
            )
          }
        }
        if (claim.predicateValidationReference) {
          createXMLNode(
            xmlDoc,
            xmlStrings.predicateValidationReference,
            { [xmlStrings.id]: claim.predicateValidationReference },
            null,
            claimType,
          );
        }
      });
    }

    // Make predicates.
    if (predicateStore.predicates && predicateStore.predicates.length > 0) {
      let predicates = createXMLNode(xmlDoc, xmlStrings.predicates, {}, null, buildingBlocks);
      predicateStore.predicates.forEach((p) => {
        let pAttrs: JE_XMLElementAttributes = {
          [xmlStrings.id]: p.id,
          [xmlStrings.method]: p.method,
        };
        if (p.errorMessage) {
          pAttrs[xmlStrings.helpText] = p.errorMessage;
        }
        let predicate = createXMLNode(xmlDoc, xmlStrings.predicate, pAttrs, null, predicates);
        let parameters = createXMLNode(xmlDoc, xmlStrings.parameters, {}, null, predicate);
        p.parameters.forEach((param) => {
          createXMLNode(xmlDoc, xmlStrings.parameter, { [xmlStrings.id]: param.id }, param.value, parameters);
        });
      });
    }

    // Make predicate validations.
    if (pValidationStore.pValidations && pValidationStore.pValidations.length > 0) {
      let predicateValidations = createXMLNode(xmlDoc, xmlStrings.predicateValidations, {}, null, buildingBlocks);
      pValidationStore.pValidations.forEach((pv) => {
        let predicateValidation = createXMLNode(
          xmlDoc,
          xmlStrings.predicateValidation,
          { [xmlStrings.id]: pv.id },
          null,
          predicateValidations,
        );
        let predicateGroups = createXMLNode(xmlDoc, xmlStrings.predicateGroups, {}, null, predicateValidation);
        pv.groups.forEach((pg) => {
          let predicateGroup = createXMLNode(
            xmlDoc,
            xmlStrings.predicateGroup,
            { [xmlStrings.id]: pg.id },
            null,
            predicateGroups,
          );
          if (pg.userHelpText) {
            createXMLNode(xmlDoc, xmlStrings.userHelpText, {}, pg.userHelpText, predicateGroup);
          }
          let prAttrs: JE_XMLElementAttributes = {};
          if (pg.matchAtLeast !== undefined) {
            prAttrs[xmlStrings.matchAtLeast] = pg.matchAtLeast.toString();
          }
          let predicateReferences = createXMLNode(
            xmlDoc,
            xmlStrings.predicateReferences,
            prAttrs,
            null,
            predicateGroup,
          );
          pg.predicateReferences.forEach((pid: string) => {
            createXMLNode(xmlDoc, xmlStrings.predicateReference, { [xmlStrings.id]: pid }, null, predicateReferences);
          });
        });
      });
    }

    // Make claim transforms.
    if (claimXforms.claimXforms && Object.keys(claimXforms.claimXforms).length > 0) {
      let claimsTransformations = createXMLNode(xmlDoc, xmlStrings.claimsTransformations, {}, null, buildingBlocks);
      Object.keys(claimXforms.claimXforms).forEach((xformId: string) => {
        let xform: JE_FQClaimXformSpec = claimXforms.claimXforms[xformId];
        let claimsTransformation = createXMLNode(
          xmlDoc,
          xmlStrings.claimsTransformation,
          { [xmlStrings.id]: xform.id, [xmlStrings.transformationMethod]: xform.method },
          null,
          claimsTransformations,
        );
        if (xform.inputClaims && xform.inputClaims.length > 0) {
          let inputClaims = createXMLNode(xmlDoc, xmlStrings.inputClaims, {}, null, claimsTransformation);
          xform.inputClaims.forEach((iClaimInfo: JE_claimXformIOClaimSpec) => {
            createXMLNode(
              xmlDoc,
              xmlStrings.inputClaim,
              {
                [xmlStrings.claimTypeReferenceId]: iClaimInfo.claimId,
                [xmlStrings.transformationClaimType]: iClaimInfo.xformClaimType,
              },
              null,
              inputClaims,
            );
          });
        }
        if (xform.inputParameters && xform.inputParameters.length > 0) {
          let inputParameters = createXMLNode(xmlDoc, xmlStrings.inputParameters, {}, null, claimsTransformation);
          xform.inputParameters.forEach((paramInfo: JE_claimXformParamSpec) => {
            createXMLNode(
              xmlDoc,
              xmlStrings.inputParameter,
              {
                [xmlStrings.id]: paramInfo.id,
                [xmlStrings.dataType]: paramInfo.dataType,
                [xmlStrings.value]: paramInfo.value,
              },
              null,
              inputParameters,
            );
          });
        }
        if (xform.outputClaims && xform.outputClaims.length > 0) {
          let outputClaims = createXMLNode(xmlDoc, xmlStrings.outputClaims, {}, null, claimsTransformation);
          xform.outputClaims.forEach((oClaimInfo: JE_claimXformIOClaimSpec) => {
            createXMLNode(
              xmlDoc,
              xmlStrings.outputClaim,
              {
                [xmlStrings.claimTypeReferenceId]: oClaimInfo.claimId,
                [xmlStrings.transformationClaimType]: oClaimInfo.xformClaimType,
              },
              null,
              outputClaims,
            );
          });
        }
      });
    }

    // Make client definitions (???)
    if (clientDefinitionStore.clientDefinitionIds.size > 0) {
      let clientDefinitions = createXMLNode(xmlDoc, xmlStrings.clientDefinitions, {}, null, buildingBlocks);
      clientDefinitionStore.clientDefinitions.forEach((cd) => {
        let clientDefinition = createXMLNode(
          xmlDoc,
          xmlStrings.clientDefinition,
          { [xmlStrings.id]: cd.id },
          null,
          clientDefinitions,
        );
        createXMLNode(xmlDoc, xmlStrings.clientUIFilterFlags, {}, cd.uiFilterFlags, clientDefinition);
      });
    }

    // Make content definitions.
    if (
      contentDefinitionStore.contentDefinitions &&
      Object.keys(contentDefinitionStore.contentDefinitions).length > 0
    ) {
      let contentDefinitions = createXMLNode(xmlDoc, xmlStrings.contentDefinitions, {}, null, buildingBlocks);
      Object.keys(contentDefinitionStore.contentDefinitions).forEach((cdId: string) => {
        let contentDef = contentDefinitionStore.contentDefinitions[cdId];
        let contentDefinition = createXMLNode(
          xmlDoc,
          xmlStrings.contentDefinition,
          { [xmlStrings.id]: contentDef.id },
          null,
          contentDefinitions,
        );
        createXMLNode(xmlDoc, xmlStrings.loadUri, {}, contentDef.loadUri, contentDefinition);
        createXMLNode(xmlDoc, xmlStrings.recoveryUri, {}, contentDef.recoveryUri, contentDefinition);
        createXMLNode(xmlDoc, xmlStrings.dataUri, {}, contentDef.dataUri, contentDefinition);
        if (contentDef.metadata && contentDef.metadata.length > 0) {
          let metadata = createXMLNode(xmlDoc, xmlStrings.metadata, {}, null, contentDefinition);
          contentDef.metadata.forEach((mdata) => {
            createXMLNode(xmlDoc, xmlStrings.item, { [xmlStrings.key]: mdata.key }, mdata.value, metadata);
          });
        }
        if (contentDef.localizedResourcesReferences) {
          let localizedResourcesReferences = createXMLNode(
            xmlDoc,
            xmlStrings.localizedResourcesReferences,
            { [xmlStrings.mergeBehavior]: contentDef.localizedResourcesReferences.mergeBehavior },
            null,
            contentDefinition,
          );
          contentDef.localizedResourcesReferences.references.forEach((lrr) => {
            createXMLNode(
              xmlDoc,
              xmlStrings.localizedResourcesReference,
              { [xmlStrings.language]: lrr.language, [xmlStrings.localizedResourcesReferenceId]: lrr.referenceId },
              null,
              localizedResourcesReferences,
            );
          });
        }
      });
    }

    // Make the localization.
    if (localizationStore.enabled) {
      let localization = createXMLNode(
        xmlDoc,
        xmlStrings.localization,
        { [xmlStrings.enabled]: localizationStore.enabled },
        null,
        buildingBlocks,
      );
      let supportedLanguages = createXMLNode(
        xmlDoc,
        xmlStrings.supportedLanguages,
        {
          [xmlStrings.defaultLanguage]: localizationStore.supportedLanguages.defaultLanguage,
          [xmlStrings.mergeBehavior]: localizationStore.supportedLanguages.mergeBehavior,
        },
        null,
        localization,
      );
      localizationStore.supportedLanguages.supportedLanguages.forEach((sl) => {
        createXMLNode(xmlDoc, xmlStrings.supportedLanguage, {}, sl, supportedLanguages);
      });
      Object.keys(localizationStore.localizedResources).forEach((lrId) => {
        let lr = localizationStore.localizedResources[lrId];
        let localizedResources = createXMLNode(
          xmlDoc,
          xmlStrings.localizedResources,
          { [xmlStrings.id]: lr.id },
          null,
          localization,
        );
        let localizedStrings = createXMLNode(xmlDoc, xmlStrings.localizedStrings, {}, null, localizedResources);
        lr.localizedStrings.forEach((ls) => {
          let lsAttrs: JE_XMLElementAttributes = {
            [xmlStrings.elementType]: ls.elementType,
          };
          if (ls.elementId !== undefined) {
            lsAttrs[xmlStrings.elementId] = ls.elementId;
          }
          lsAttrs[xmlStrings.stringId] = ls.stringId;
          createXMLNode(xmlDoc, xmlStrings.localizedString, lsAttrs, ls.value, localizedStrings);
        });
      });
    }

    // Make display controls.
    if (displayControlStore && displayControlStore.displayControls.length > 0) {
      let displayControls = createXMLNode(xmlDoc, xmlStrings.displayControls, {}, null, buildingBlocks);
      displayControlStore.displayControls.forEach((dc: JE_FQDisplayControlSpec) => {
        let displayControl = createXMLNode(
          xmlDoc,
          xmlStrings.displayControl,
          { [xmlStrings.id]: dc.id, [xmlStrings.userInterfaceControlType]: dc.userInterfaceControlType },
          null,
          displayControls,
        );
        if (dc.inputClaims && dc.inputClaims.length > 0) {
          generateTPClaimRefFromFQSpecs(
            xmlDoc,
            xmlStrings.inputClaims,
            xmlStrings.inputClaim,
            dc.inputClaims,
            displayControl,
          );
        }
        if (dc.displayClaims && dc.displayClaims.length > 0) {
          generateTPClaimRefFromFQSpecs(
            xmlDoc,
            xmlStrings.displayClaims,
            xmlStrings.displayClaim,
            dc.displayClaims,
            displayControl,
          );
        }
        if (dc.outputClaims && dc.outputClaims.length > 0) {
          generateTPClaimRefFromFQSpecs(
            xmlDoc,
            xmlStrings.outputClaims,
            xmlStrings.outputClaim,
            dc.outputClaims,
            displayControl,
          );
        }
        if (dc.actions && dc.actions.length > 0) {
          let actions = createXMLNode(xmlDoc, xmlStrings.actions, {}, null, displayControl);
          dc.actions.forEach((act: JE_DisplayControlAction) => {
            let action = createXMLNode(xmlDoc, xmlStrings.action, { [xmlStrings.id]: act.id }, null, actions);
            if (act.validationClaimsExchange && act.validationClaimsExchange.length > 0) {
              let validationClaimsExchange = createXMLNode(
                xmlDoc,
                xmlStrings.validationClaimsExchange,
                {},
                null,
                action,
              );
              act.validationClaimsExchange.forEach((vceTPref: JE_ValidationTechnicalProfileRef) => {
                let validationClaimsExchangeTechnicalProfile = createXMLNode(
                  xmlDoc,
                  xmlStrings.validationClaimsExchangeTechnicalProfile,
                  { [xmlStrings.technicalProfileReferenceId]: vceTPref.profileId },
                  null,
                  validationClaimsExchange,
                );
                if (vceTPref.preconditions && vceTPref.preconditions.length > 0) {
                  let preconditions = createXMLNode(
                    xmlDoc,
                    xmlStrings.preconditions,
                    {},
                    null,
                    validationClaimsExchangeTechnicalProfile,
                  );
                  vceTPref.preconditions.forEach((prec: JE_FQPreconditionSpec) => {
                    let precondition = createXMLNode(
                      xmlDoc,
                      xmlStrings.precondition,
                      { [xmlStrings.type]: prec.type, [xmlStrings.executeActionsIf]: prec.executeActionsIf },
                      null,
                      preconditions,
                    );
                    prec.values.forEach((v: string) => {
                      createXMLNode(xmlDoc, xmlStrings.value, {}, v, precondition);
                    });
                    createXMLNode(xmlDoc, xmlStrings.action, {}, prec.action, precondition);
                  });
                }
              });
            }
          });
        }
      });
    }
  }

  // Make claims providers and technical profiles.
  let claimsProviders = createXMLNode(xmlDoc, xmlStrings.claimsProviders, {}, null, tfpRoot);
  Object.keys(technicalProfileStore.claimsProviders).forEach((providerName: string) => {
    let claimsProvider = createXMLNode(xmlDoc, xmlStrings.claimsProvider, {}, null, claimsProviders);
    if (technicalProfileStore.claimsProviders[providerName].domain) {
      createXMLNode(
        xmlDoc,
        xmlStrings.domain,
        {},
        technicalProfileStore.claimsProviders[providerName].domain,
        claimsProvider,
      );
    }
    createXMLNode(xmlDoc, xmlStrings.displayName, {}, providerName, claimsProvider);
    let technicalProfiles = createXMLNode(xmlDoc, xmlStrings.technicalProfiles, {}, null, claimsProvider);
    Object.keys(technicalProfileStore.claimsProviders[providerName].technicalProfiles).forEach((tpId: string) => {
      generateTechnicalProfileXMLFromFQSpec(
        xmlDoc,
        technicalProfileStore.claimsProviders[providerName].technicalProfiles[tpId],
        technicalProfiles,
      );
    });
  });

  // Make user journeys.
  let userJourneys = createXMLNode(xmlDoc, xmlStrings.userJourneys, {}, null, tfpRoot);
  Object.keys(userJourneyStore.userJourneys).forEach((journeyId: string) => {
    let journey = userJourneyStore.userJourneys[journeyId];
    let userJourney = createXMLNode(
      xmlDoc,
      xmlStrings.userJourney,
      { [xmlStrings.id]: journey.id },
      null,
      userJourneys,
    );
    if (journey.authorizationTechnicalProfileId) {
      let authorization = createXMLNode(xmlDoc, xmlStrings.authorization, {}, null, userJourney);
      let authorizationTechnicalProfiles = createXMLNode(
        xmlDoc,
        xmlStrings.authorizationTechnicalProfiles,
        {},
        null,
        authorization,
      );
      createXMLNode(
        xmlDoc,
        xmlStrings.authorizationTechnicalProfile,
        { [xmlStrings.referenceId]: journey.authorizationTechnicalProfileId },
        null,
        authorizationTechnicalProfiles,
      );
    }
    let orchestrationSteps = createXMLNode(xmlDoc, xmlStrings.orchestrationSteps, {}, null, userJourney);
    journey.steps.forEach((step: JE_FQOrchestrationStepSpec) => {
      let oStepAttrs: { [index: string]: string } = {
        [xmlStrings.order]: step.order.toString(),
        [xmlStrings.type]: step.type,
      };
      if (step.contentDefinitionReferenceId) {
        oStepAttrs[xmlStrings.contentDefinitionReferenceId] = step.contentDefinitionReferenceId;
      }
      if (step.cpimIssuerTechnicalProfileReferenceId) {
        oStepAttrs[xmlStrings.cpimIssuerTechnicalProfileReferenceId] = step.cpimIssuerTechnicalProfileReferenceId;
      }
      let orchestrationStep = createXMLNode(xmlDoc, xmlStrings.orchestrationStep, oStepAttrs, null, orchestrationSteps);
      generatePreconditionListXML(xmlDoc, step.preconditions, orchestrationStep);
      if (step.claimsProviderSelections && step.claimsProviderSelections.length > 0) {
        let claimsProviderSelections = createXMLNode(
          xmlDoc,
          xmlStrings.claimsProviderSelections,
          {},
          null,
          orchestrationStep,
        );
        step.claimsProviderSelections.forEach((cpsel: JE_FQClaimsProviderSelectionSpec) => {
          let cpselAttrs: { [index: string]: string } = {};
          if (cpsel.targetClaimsExchangeId) {
            cpselAttrs[xmlStrings.targetClaimsExchangeId] = cpsel.targetClaimsExchangeId;
          }
          if (cpsel.validationClaimsExchangeId) {
            cpselAttrs[xmlStrings.validationClaimsExchangeId] = cpsel.validationClaimsExchangeId;
          }
          createXMLNode(xmlDoc, xmlStrings.claimsProviderSelection, cpselAttrs, null, claimsProviderSelections);
        });
      }
      if (step.claimsExchanges && step.claimsExchanges.length > 0) {
        let claimsExchanges = createXMLNode(xmlDoc, xmlStrings.claimsExchanges, {}, null, orchestrationStep);
        step.claimsExchanges.forEach((cx: JE_FQClaimsExchangeSpec) => {
          createXMLNode(
            xmlDoc,
            xmlStrings.claimsExchange,
            { [xmlStrings.id]: cx.id, [xmlStrings.technicalProfileReferenceId]: cx.technicalProfileReferenceId },
            null,
            claimsExchanges,
          );
        });
      }
    });
    // TODO: This may not be needed for all user journeys, or require a custom journey.
    createXMLNode(xmlDoc, xmlStrings.clientDefinition, { [xmlStrings.referenceId]: 'DefaultWeb' }, null, userJourney);
  });

  return new XMLSerializer().serializeToString(xmlDoc.documentElement);
};
